import { Navbar, SearchInput } from '@/components';
import {
  ButtonConfig,
  DropdownConfig,
} from '@/components/customByConfiguration';
import { PageSettingsContext } from '@/contexts';
import { ENTITY, QueryKey, ROLE_NAME, UserActions } from '@/enums';
import { useFetchRoles, useParseQueryString, useQueryNavigate } from '@/hooks';
import useCheckScopeCombinations from '@/hooks/useCheckScopeCombinations';
import { useGetMe } from '@/hooks/useGetProfileQuery';
import { useToggleModal } from '@/hooks/useToggleModal';
import { AdministratorDataType, QueryParams } from '@/interfaces';
import { PRIVATE_ROUTES } from '@/routes';
import { UserService } from '@/services';
import { CustomError, errorNotification, rowKey } from '@/utils';
import {
  AmiliPagination,
  CustomBadge,
  CustomButton,
  CustomColumnType,
  CustomModal,
  CustomTable,
  DEFAULT_PAGE_SIZE,
  Icon,
  InformativeModal,
  PhotoUser,
} from '@amili-v3/amili-v3-design';
import { icons } from '@amili-v3/amili-v3-design/src/icons/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Space, Tooltip, notification } from 'antd';
import _get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import { useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { getScrollY } from '../Order/utils';
import { PageLabel, SharedContentWrapper, SharedPageWrapper } from '../styles';
import AddNewAdminForm from './AddNewAdminForm';
import AdminFilter from './AdminFilter';
import AssignRoleForm from './AssignRoleForm';
import { NameWrapper } from './styles';
import './styles.scss';
import { getOwnUserRoleLevel, getUserRoleLevel } from './utils';

export const getAdminStyle = (name: string) => {
  switch (name) {
    case ROLE_NAME.CUSTOMER_SUPPORT:
      return {
        color: 'var(--link)',
        icon: 'box',
        gradient: ['#00AEC7 ', '#2B36BA'],
      };
    case ROLE_NAME.LAB_STAFF_USER:
      return {
        color: 'var(--primary)',
        icon: 'laboratory',
        gradient: ['#DA2E9A', '#FF6F59'],
      };
    case ROLE_NAME.ADMIN:
    case ROLE_NAME.SUPER_ADMIN:
      return {
        color: 'var(--warning)',
        icon: 'starFilled',
        gradient: ['#FFAC00', '#FF3D00'],
      };
    default:
      return {
        color: 'var(--secondary)',
        icon: 'outside',
        gradient: ['#2B36BA', '#DA2E9A'],
      };
  }
};

const AdminManagement = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const queryClient = useQueryClient();
  const handleNavigate = useQueryNavigate();
  const qsParams = useParseQueryString<QueryParams>();
  const { pageSettings } = useContext(PageSettingsContext);
  const pageSize = pageSettings?.[pathname];
  const { data: profile } = useGetMe();
  const roleLevel = getOwnUserRoleLevel(profile?.mainRole);

  const { data: allRoles, isLoading: isLoadingRoles } = useFetchRoles();

  const {
    canCreateUsers: canCreate,
    canDeleteUsers: canDelete,
    canUpdateUsers: canAssignRole,
  } = useCheckScopeCombinations([
    [{ entity: ENTITY.USERS, action: UserActions.CREATE }],
    [{ entity: ENTITY.USERS, action: UserActions.DELETE }],
    [{ entity: ENTITY.USERS, action: UserActions.UPDATE }],
  ]);
  const canSearch = true;

  const { canGetRoles: canReadRole } = useCheckScopeCombinations([
    [{ entity: ENTITY.ROLE, action: UserActions.GET }],
  ]);
  const { canGetRoles: canViewRoleColumn } = useCheckScopeCombinations([
    [{ entity: ENTITY.ROLE, action: UserActions.GET }],
  ]);

  const { adminModal } = useToggleModal(['admin']);
  const [assignRole, setAssignRole] = useState('');
  const [admin, setAdmin] = useState<Record<string, any>>({});
  const mutationDelete = useMutation({
    mutationFn: UserService.deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QueryKey.ADMINISTRATOR.ALL] });
      notification.success({ message: t('common.success') });
      setAdmin({});
    },
    onError: error =>
      errorNotification({ error: error as unknown as CustomError }),
  });

  const handleDelete = () => {
    return mutationDelete.mutateAsync(admin?.id);
  };

  const queryParams = useMemo(() => {
    if (isEmpty(qsParams) || !qsParams.roles)
      return {
        page: 1,
        take: pageSize || DEFAULT_PAGE_SIZE,
        ...qsParams,
        deleted: false,
        roles: allRoles?.map(role => role.id),
      };
    return {
      ...qsParams,
      deleted: false,
      take: Number(qsParams.take),
      page: Number(qsParams.page),
    };
  }, [qsParams, pageSize, allRoles]);

  const { isLoading, data } = useQuery({
    queryKey: [QueryKey.ADMINISTRATOR.ALL, queryParams],
    queryFn: UserService.getUsers,
    meta: { showError: true },
    enabled: !isLoadingRoles,
  });

  const dataSource: AdministratorDataType[] = useMemo(
    () => _get(data, 'data.data', []),
    [data],
  );
  const total: number = useMemo(() => _get(data, 'data.itemCount', 0), [data]);

  const renderDropdown = (_: any, record: any) => {
    const isOwnUserAccount = record.id === profile?.id;
    const { userRoleLevel, userRoleName } = getUserRoleLevel(record);
    const hasPermission = roleLevel <= userRoleLevel;

    const DropdownButton = (
      <div>
        <CustomButton
          type="white"
          aria-label="actions"
          prefix={<Icon name="moreVertical" />}
          disabled={isOwnUserAccount || !hasPermission}
        />
      </div>
    );

    return (
      <DropdownConfig
        menu={{
          items: [
            canAssignRole
              ? {
                  key: 'assign-role-on-row',
                  label: t('administrator.assignRole'),
                  icon: <Icon name="pen" color="var(--primary)" />,
                  onClick: () => setAssignRole(record.id),
                }
              : null,
            canDelete
              ? {
                  key: 'delete-user-on-row',
                  label: t('common.delete'),
                  icon: <Icon name="trash" color="var(--primary)" />,
                  onClick: () => setAdmin(record),
                }
              : null,
          ],
        }}
      >
        {hasPermission ? (
          DropdownButton
        ) : (
          <div>
            <Tooltip
              title={t('administrator.cannotAdjustHigherRoleLevel', {
                ROLE_NAME: t(`roles.${userRoleName}`),
              })}
            >
              {DropdownButton}
            </Tooltip>
          </div>
        )}
      </DropdownConfig>
    );
  };

  const columns: CustomColumnType<AdministratorDataType>[] = [
    {
      title: t('common.name'),
      dataIndex: 'name',
      render: (text: string, record: any) => {
        const { gradient, icon } = getAdminStyle(
          record?.userRoles?.[0]?.role?.name,
        );
        return (
          <NameWrapper>
            <PhotoUser
              primary
              iconWidth={14}
              iconHeight={14}
              gradient={gradient}
              icon={icon as keyof typeof icons}
            />{' '}
            {record.firstName} {record.lastName}
          </NameWrapper>
        );
      },
    },
    { title: t('common.emailAddress'), dataIndex: 'email', key: 'email' },
    canViewRoleColumn
      ? {
          title: t('common.roles'),
          dataIndex: 'userRoles',
          render: (userRoles: Record<string, any>[]) => {
            return (
              <Space wrap>
                {userRoles?.map(userRole => {
                  const style = getAdminStyle(userRole?.role?.name);
                  const { color, icon } = style;
                  return (
                    <CustomBadge
                      color={color}
                      key={userRole?.role?.id}
                      prefix={
                        <Icon
                          width={14}
                          height={14}
                          name={icon as keyof typeof icons}
                        />
                      }
                    >
                      {i18n.exists(`roles.${userRole?.role?.name}`)
                        ? t(`roles.${userRole?.role?.name}`)
                        : userRole?.role?.name}
                    </CustomBadge>
                  );
                })}
              </Space>
            );
          },
        }
      : {},
    {
      title: <Icon name="moreVertical" height={14} width={14} />,
      width: 64,
      className: 'actions',
      render: renderDropdown,
    },
  ];

  const left = [
    <PageLabel
      key="title"
      onClick={() => navigate(PRIVATE_ROUTES.ADMINISTRATOR.ROOT)}
    >
      {t(`titles.administrator`)}
    </PageLabel>,
    canCreate && (
      <ButtonConfig
        id="add-new-admin-tab"
        key={'add-new-admin-tab'}
        type="white"
        onClick={() => adminModal.open()}
        prefix={<Icon name="addCircle" width={16} height={16} />}
      >
        {t('administrator.addNewAdmin')}
      </ButtonConfig>
    ),
    canReadRole && (
      <ButtonConfig
        id="manage-role-permission-tab"
        key={'manage-role-permission-tab'}
        type="white"
        prefix={<Icon name="pen" width={12} />}
        onClick={() => navigate(PRIVATE_ROUTES.ADMINISTRATOR.ROLE)}
      >
        {t('administrator.manageRoles&Permission')}
      </ButtonConfig>
    ),
  ];

  const right = [canSearch && <SearchInput />, <AdminFilter key="filter" />];

  return (
    <SharedPageWrapper>
      <Navbar left={left} right={right} />

      <SharedContentWrapper>
        <CustomTable
          rowKey={rowKey}
          columns={columns}
          loading={isLoading}
          dataSource={dataSource}
          scroll={{ y: getScrollY() }}
          className="administrator-custom-table"
        />

        <AmiliPagination
          total={total}
          defaultPageSize={pageSize}
          onChange={(page, take) => handleNavigate({ page, take })}
        />
      </SharedContentWrapper>

      <CustomModal
        open={!!assignRole}
        footer={null}
        title={t('administrator.assignRole')}
        onCancel={() => setAssignRole('')}
      >
        <AssignRoleForm assignRole={assignRole} setAssignRole={setAssignRole} />
      </CustomModal>

      <CustomModal
        open={adminModal.visible}
        footer={null}
        title={t('administrator.addNewAdmin')}
        onCancel={() => adminModal.close()}
      >
        <AddNewAdminForm closeModal={adminModal.close} />
      </CustomModal>

      <InformativeModal
        error
        title=""
        centered
        layout="vertical"
        open={!isEmpty(admin)}
        onCancel={() => setAdmin({})}
        icon={<Icon name="trash" width={30} />}
        footer={[
          <CustomButton
            size="large"
            key="confirm"
            onClick={handleDelete}
            loading={mutationDelete.isPending}
          >
            {t('common.confirm')}
          </CustomButton>,
        ]}
      >
        {t('administrator.confirmDelete', { email: admin?.email })}
      </InformativeModal>
    </SharedPageWrapper>
  );
};

export default AdminManagement;
